import React, { useState, useEffect } from 'react';
import { Loading } from '../../../components/loading/Loading';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { Card, CardContent, Tooltip, Typography, Table, TableBody, TableCell, TableContainer, TableRow, IconButton, Link, Grid, Stack, Menu, MenuItem, ListItemIcon, useMediaQuery, Button } from '@mui/material';
import { Edit, Delete, MoreVert, AttachFile } from '@mui/icons-material';
import AddDocumentModal from '../../../components/documents/modals/AddDocumentModal';
import { useUserContext } from '../../../context/user_context/UserContext';
import { deleteOrganizationDocumentAndReferenceFromFirestore, getOrganizationDocumentDownloadURL, organizationDocumentsSnapshotListener } from '../../../repository/DocumentsDAO';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import EditOrganizationModal from './modals/EditOrganizationModal';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import ImageUploadModal from '../../../components/image_upload_modal/ImageUploadModal';
import { ImageUploadType } from '../../../components/image_upload_modal/ImageUploadType';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';
function Organization() {
    const { organization } = useOrganizationContext();
    const { firebaseUser } = useUserContext();
    const { hasPermission } = usePermissions();
    const [documents, setDocuments] = useState([]);
    const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
    const [showEditOrganizationModal, setShowEditOrganizationModal] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [showAddOrganizationLogoModal, setShowAddOrganizationLogoModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { t } = useTranslation();
    const { showToast } = useToast();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [showAddImageModal, setShowAddImageModal] = useState(false);

    useEffect(() => {
        if (firebaseUser) {
            organizationDocumentsSnapshotListener(firebaseUser.organization, setDocuments);
        }
    }, [firebaseUser]);

    const handleDeleteDocument = async () => {
        setConfirmOpen(false);
        try {
            const result = await deleteOrganizationDocumentAndReferenceFromFirestore(
                firebaseUser.organization,
                docToDelete.id,
                docToDelete.name
            );
            
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, {x: docToDelete.name}), ToastType.SUCCESS);
            } else {
                showToast(t(StringKeys.FAILED_TO_DELETE_X, {x: docToDelete.name}), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_DELETE_X, {x: docToDelete.name}), ToastType.DANGER);
        }
    };
    return (
        <>
            {organization ? (
                <AppSidebar
                    content={
                        <>
                            <Stack gap={4}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item>
                                        {organization.logo_url ? (
                                            <img
                                                src={organization.logo_url}
                                                alt="Profile"
                                                style={{
                                                    width: '80px',
                                                    height: '80px',
                                                    objectFit: 'cover',
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => { setShowAddOrganizationLogoModal(true) }}
                                            />
                                        ) : (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundColor: '#007bff',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                width: '80px',
                                                fontSize: '2rem',
                                                height: '80px',
                                                cursor: "pointer"
                                            }}
                                                onClick={() => { setShowAddOrganizationLogoModal(true) }}
                                            >
                                                {organization.name[0].toUpperCase()}
                                            </div>
                                        )}
                                    </Grid>
                                    <Grid item xs>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            {!isMobile && hasPermission(Modules.ORGANIZATION, PermissionTypes.WRITE) && (
                                                <>
                                                    <Button variant="text" startIcon={<Edit />} onClick={() => setShowEditOrganizationModal(true)}>
                                                        {t(StringKeys.EDIT)}
                                                    </Button>
                                                    <Button variant="text" startIcon={<AttachFile />} onClick={() => setShowAddDocumentModal(true)}>
                                                            {t(StringKeys.ADD_DOCUMENT)}
                                                    </Button>
                                                </>       
                                            )}
                                            {isMobile && (
                                                <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                                    <MoreVert />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Grid>
                                </Grid>

                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant={isMobile ? "h6" : "h5"}>{organization.name}</Typography>
                                        <Typography>
                                            {t(StringKeys.ADDRESS)}: {organization.address} {organization.city}, {organization.state} {organization.zip}
                                        </Typography>
                                        <Typography>
                                            {t(StringKeys.PHONE)}: {organization.phone_number}
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.COMPANY_DOCUMENTS)}</Typography>
                                        <TableContainer>
                                            <Table aria-label="documents table">
                                                <TableBody>
                                                {
                                                    documents.length > 0 ? (
                                                    documents.map((doc) => (
                                                        <TableRow key={doc.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        <TableCell component="th" scope="row">
                                                            <Link 
                                                                onClick={async (e) => {
                                                                    e.preventDefault();
                                                                    try {
                                                                        var result = await getOrganizationDocumentDownloadURL(
                                                                            firebaseUser.organization,
                                                                            doc.id
                                                                        );
                                                                        if (result.success) {
                                                                            window.open(result.data, '_blank');
                                                                        } else {
                                                                            showToast(t(StringKeys.FAILED_TO_LOAD_DOCUMENT), ToastType.DANGER);
                                                                        }
                                                                    } catch (error) {
                                                                        console.log(error);
                                                                        showToast(t(StringKeys.FAILED_TO_LOAD_DOCUMENT), ToastType.DANGER);
                                                                    }
                                                                }}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                {doc.title}
                                                            </Link>
                                                        </TableCell>
                                                        {hasPermission(Modules.ORGANIZATION, PermissionTypes.WRITE) && (
                                                            <TableCell align="right">
                                                                <IconButton 
                                                                    onClick={() => { 
                                                                        setDocToDelete(doc); 
                                                                        setConfirmOpen(true); 
                                                                    }} 
                                                                    aria-label="delete"
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </TableCell>
                                                        )}
                                                        </TableRow>
                                                    ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={3} align="center">
                                                                <Typography variant="body2">{t(StringKeys.NO_DOCUMENTS_TO_SHOW)}</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                }
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Stack>

                            {/* Dropdown Menu for Mobile */}
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => handleMenuClose(setAnchorEl)}
                            >
                                <MenuItem onClick={() => { setShowEditOrganizationModal(true); handleMenuClose(setAnchorEl); }}>
                                    <ListItemIcon>
                                        <Edit fontSize="small" />
                                    </ListItemIcon>
                                    {t(StringKeys.EDIT)}
                                </MenuItem>
                                <MenuItem onClick={() => { setShowAddDocumentModal(true); handleMenuClose(setAnchorEl); }}>
                                    <ListItemIcon>
                                        <AttachFile fontSize="small" />
                                    </ListItemIcon>
                                    {t(StringKeys.ADD_DOCUMENT)}
                                </MenuItem>
                            </Menu>

                            <AddDocumentModal
                                show={showAddDocumentModal}
                                onHide={() => setShowAddDocumentModal(false)}
                                organization={firebaseUser.organization}
                                type={"Organization Document"}
                                employee={firebaseUser.uid}
                            />
                            <ConfirmDeleteModal
                                open={confirmOpen}
                                onClose={() => setConfirmOpen(false)}
                                onConfirm={() => handleDeleteDocument(docToDelete.id, docToDelete.name)}
                                item={docToDelete ? docToDelete.title : null}
                            />
                            <EditOrganizationModal
                                show={showEditOrganizationModal}
                                onHide={() => setShowEditOrganizationModal(false)}
                                organization={organization}
                            />
                            <ImageUploadModal
                                show={showAddOrganizationLogoModal}
                                onHide={() => { setShowAddOrganizationLogoModal(false) }}
                                organization={firebaseUser.organization}
                                modalTitle={t(StringKeys.ADD_NEW_IMAGE)}
                                cropShape="rect"
                                item
                                uploadType={ImageUploadType.OrganizationImage}
                            />
                            {/* <AddOrganizationLogoModal
                                show={showAddOrganizationLogoModal}
                                onHide={() => { setShowAddOrganizationLogoModal(false) }}
                            /> */}
                        </>
                    }
                />
            ) : (
                <AppSidebar content={<Loading />} />
            )}
        </>
    );
}

export default Organization;
